import { DatePicker } from "$components/inputs";
import { makeStyles } from "$components/mui-styles";
import RHFTextField from "$components/rhf/text-field";
import { Grid } from "@material-ui/core";
import { Controller, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import {
  ADMISSION_DATE,
  DISCHARGE_DATE,
  INPATIENT_DETAILS,
  INPATIENT_NUMBER,
  WARD,
} from "./formConstants";
import type { FormValues } from "./patientTypeFormHelpers";

const useStyles = makeStyles((theme) => ({
  label: {
    color: theme.palette.grey[600],
  },
  ward: {
    width: "100%",
  },
}));

export const InpatientForm: FC = () => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const { control, getValues } = useFormContext<FormValues>();

  const WARD_FIELD = `${INPATIENT_DETAILS}.${WARD}`;
  const INPATIENT_NUMBER_FIELD =
    `${INPATIENT_DETAILS}.${INPATIENT_NUMBER}` as const;
  const ADMISSION_DATE_FIELD =
    `${INPATIENT_DETAILS}.${ADMISSION_DATE}` as const;
  const DISCHARGE_DATE_FIELD =
    `${INPATIENT_DETAILS}.${DISCHARGE_DATE}` as const;

  const wardMsg = formatMessage({
    id: "invoicing.inpatient.ward",
    defaultMessage: "Ward",
  });
  const inpatientNumberMsg = formatMessage({
    id: "invoicing.inpatient.inpatientNumber",
    defaultMessage: "Patient Number",
  });
  const admissionDateMsg = formatMessage({
    id: "invoicing.inpatient.admissionDate",
    defaultMessage: "Admission Date",
  });
  const dischargeDateMsg = formatMessage({
    id: "invoicing.inpatient.dischargeDate",
    defaultMessage: "Discharge Date",
  });

  return (
    <Grid container>
      <Grid container>
        <Grid item xs={8}>
          <RHFTextField
            name={WARD_FIELD}
            required
            label={wardMsg}
            placeholder={wardMsg}
            data-testid="invoicing.ward.field"
            InputLabelProps={{
              classes: { root: classes.label },
            }}
            inputProps={{ min: "1" }}
            className={classes.ward}
          />
        </Grid>
        <Grid item xs={4}>
          <RHFTextField
            name={INPATIENT_NUMBER_FIELD}
            required
            label={inpatientNumberMsg}
            placeholder={inpatientNumberMsg}
            data-testid="invoicing.inpatientNumber.field"
            InputLabelProps={{
              classes: { root: classes.label },
            }}
            inputProps={{
              min: "1",
            }}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item>
          <Controller
            control={control}
            name={ADMISSION_DATE_FIELD}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { error },
            }) => (
              <DatePicker
                label={admissionDateMsg}
                onChange={onChange}
                onBlur={onBlur}
                value={value ?? null}
                error={error?.message}
                aria-labelledby={admissionDateMsg}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
                data-testid="invoicing.inpatient.dateOfAdmission"
              />
            )}
          />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name={DISCHARGE_DATE_FIELD}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { error },
            }) => (
              <DatePicker
                name={DISCHARGE_DATE_FIELD}
                label={dischargeDateMsg}
                onChange={onChange}
                onBlur={onBlur}
                value={value ?? null}
                error={error?.message}
                aria-labelledby={dischargeDateMsg}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
                data-testid="invoicing.inpatient.dateOfDischarge"
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
